import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import { CATEGORY_LABELS } from '@/types/movie';

interface MovieCardProps {
  movie: Movie;
  variant?: 'grid' | 'list';
}

export default function MovieCard({ movie, variant = 'grid' }: MovieCardProps) {
  const ratingText = movie.rating.toFixed(1);

  if (variant === 'list') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
      >
        <div className="relative w-24 sm:w-28 shrink-0 aspect-[2/3] overflow-hidden rounded-md bg-background-200">
          <img
            src={movie.poster}
            alt={`${movie.title} 海报`}
            title={`${movie.title} - 妖精视频影视推荐`}
            className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0 py-1">
          <h3 className="font-heading text-base font-bold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
            {movie.title}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-600">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-700">
              {CATEGORY_LABELS[movie.category]}
            </span>
            <span>{movie.year}</span>
            <span className="text-background-400">·</span>
            <span>{movie.region}</span>
            <span className="text-background-400">·</span>
            <span>{movie.duration}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {movie.genres.map((g) => (
              <span key={g} className="rounded border border-background-300 px-1.5 py-0.5 text-[11px] text-foreground-600">
                {g}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">{movie.synopsis}</p>
        </div>
        <div className="hidden sm:flex flex-col items-end justify-between shrink-0 py-1">
          <span className="font-heading text-lg font-extrabold text-accent-500">{ratingText}</span>
          <span className="inline-flex items-center gap-1 text-[11px] text-foreground-500">
            <i className="ri-fire-line"></i>
            {(movie.heat / 1000).toFixed(1)}k
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-all hover:border-primary-300 hover:-translate-y-1"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={movie.poster}
          alt={`${movie.title} 海报`}
          title={`${movie.title} - 妖精视频影视推荐`}
          className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-2 left-2 rounded-md bg-primary-500/90 px-2 py-0.5 text-[11px] font-semibold text-background-50">
          {CATEGORY_LABELS[movie.category]}
        </span>
        <span className="absolute top-2 right-2 rounded-md bg-background-50/95 px-1.5 py-0.5 font-heading text-[12px] font-extrabold text-accent-600">
          {ratingText}
        </span>
      </div>
      <div className="p-3">
        <h3 className="font-heading text-sm font-bold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
          {movie.title}
        </h3>
        <div className="mt-1 flex items-center justify-between text-[11px] text-foreground-600">
          <span>{movie.year} · {movie.region}</span>
          <span className="inline-flex items-center gap-0.5 text-foreground-500">
            <i className="ri-fire-line"></i>
            {(movie.heat / 1000).toFixed(1)}k
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {movie.genres.slice(0, 2).map((g) => (
            <span key={g} className="rounded bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium text-accent-700">
              {g}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}